import React, { useEffect, useRef } from "react";

/**
 * BackgroundAI-Blue — "same to same" neural mesh like the reference image
 * - Crisp on HiDPI (devicePixelRatio aware)
 * - Depth layers for parallax + richer look
 * - Glowing blue nodes + gradient links
 * - Cursor highlights + attract effect
 * - Lightweight; no external libs
 */
export default function BackgroundAIBlue({
  nodeCount = 140,            // total particles across layers
  linkDistance = 120,         // base link distance (scaled per layer)
  cursorLinkDistance = 160,   // max distance to draw to cursor
  layers = 3,                 // number of parallax depth layers
  speed = 0.25,               // base speed in px/frame
  hue = 25,                   // Orange theme color (#FF6A00)
  saturation = 100,           // Full saturation for vibrant orange
  lightness = 50,             // Medium lightness for orange
  background = "transparent", // Use transparent to work with your gradient
  mode = "fixed",             // fixed | absolute
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    // HiDPI support
    let w = 0, h = 0, dpr = Math.max(1, window.devicePixelRatio || 1);
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Utilities
    const rand = (a, b) => a + Math.random() * (b - a);

    // Build layered nodes for depth + parallax
    const LAYERS = [];
    const nodesPerLayer = Math.max(10, Math.floor(nodeCount / layers));

    for (let z = 0; z < layers; z++) {
      const depth = (z + 1) / layers; // 0..1 (near → far)
      const size = 1 + (1 - depth) * 1.5; // bigger when closer
      const alpha = 0.35 + (1 - depth) * 0.35; // brighter in front
      const layerSpeed = speed * (0.35 + (1 - depth) * 1.1);
      const ld = linkDistance * (0.7 + (1 - depth) * 0.9);

      // eslint-disable-next-line no-loop-func
      const nodes = Array.from({ length: nodesPerLayer }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: rand(-layerSpeed, layerSpeed),
        vy: rand(-layerSpeed, layerSpeed),
      }));

      LAYERS.push({ depth, size, alpha, nodes, ld, layerSpeed });
    }

    const cursor = { x: w * 0.55, y: h * 0.35 };
    const onMove = (e) => { cursor.x = e.clientX; cursor.y = e.clientY; };
    window.addEventListener("mousemove", onMove, { passive: true });

    // Precompute colors
    const glowNode = (a=1) => `hsla(${hue} ${saturation}% ${lightness}% / ${a})`;
    const glowSoft = (a=1) => `hsla(${hue} ${Math.min(100, saturation+5)}% ${Math.max(35, lightness-15)}% / ${a})`;
    const lineColor = (a=1) => `hsla(${hue} ${Math.max(70, saturation-10)}% ${Math.max(55, lightness-5)}% / ${a})`;

    // Soft vignette & fog sprites (for bokeh glow)
    const makeFog = (r) => {
      const fog = document.createElement("canvas");
      const fctx = fog.getContext("2d");
      fog.width = fog.height = r * 2;
      const grad = fctx.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, glowNode(0.25));
      grad.addColorStop(1, "rgba(0,0,0,0)");
      fctx.fillStyle = grad;
      fctx.beginPath();
      fctx.arc(r, r, r, 0, Math.PI * 2);
      fctx.fill();
      return fog;
    };
    const fogSprite = makeFog(140);

    // Animation
    const step = () => {
      // clear with subtle alpha to leave a tiny motion trail
      ctx.clearRect(0, 0, w, h);

      // Background fog/bokeh dabs for depth richness (draw first)
      for (let i = 0; i < 6; i++) {
        ctx.globalAlpha = 0.08;
        const fx = (Math.sin(Date.now() * 0.0002 + i) * 0.5 + 0.5) * w;
        const fy = (Math.cos(Date.now() * 0.00025 + i * 0.7) * 0.5 + 0.5) * h;
        ctx.drawImage(fogSprite, fx - 140, fy - 140);
        ctx.globalAlpha = 1;
      }

      // For each layer: update, draw links, then nodes
      LAYERS.forEach((L) => {
        const { nodes, size, alpha, ld, layerSpeed, depth } = L;

        // Update positions with gentle drift & wrap
        for (let n of nodes) {
          n.x += n.vx; n.y += n.vy;
          // slow attract towards center for cohesion
          const cx = w * 0.5 - n.x; const cy = h * 0.5 - n.y;
          n.vx += cx * 0.000003 * (1.4 - depth);
          n.vy += cy * 0.000003 * (1.4 - depth);
          if (n.x < -20) n.x = w + 20; if (n.x > w + 20) n.x = -20;
          if (n.y < -20) n.y = h + 20; if (n.y > h + 20) n.y = -20;
        }

        // Connections (distance fade)
        ctx.lineWidth = 1;
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const a = nodes[i], b = nodes[j];
            const dx = a.x - b.x, dy = a.y - b.y;
            const dist = Math.hypot(dx, dy);
            if (dist < ld) {
              const t = 1 - dist / ld; // 0..1
              ctx.strokeStyle = lineColor(0.18 * alpha * (0.6 + t * 0.8));
              ctx.globalCompositeOperation = "lighter"; // additive for glow
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
              ctx.globalCompositeOperation = "source-over";
            }
          }
        }

        // Cursor links (stronger & closer)
        for (let i = 0; i < nodes.length; i++) {
          const p = nodes[i];
          const dx = p.x - cursor.x, dy = p.y - cursor.y;
          const dist = Math.hypot(dx, dy);
          const maxd = cursorLinkDistance * (0.75 + (1 - depth) * 0.6);
          if (dist < maxd) {
            const t = 1 - dist / maxd;
            ctx.lineWidth = 1.5 + t * 1.5;
            ctx.strokeStyle = lineColor(0.28 * alpha * (0.6 + t));
            ctx.globalCompositeOperation = "lighter";
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(cursor.x, cursor.y);
            ctx.stroke();
            ctx.globalCompositeOperation = "source-over";
            // slight attraction to cursor (subtle)
            p.vx -= dx * 0.00002 * t;
            p.vy -= dy * 0.00002 * t;
          }
        }

        // Nodes with glow
        ctx.shadowColor = glowSoft(0.8 * alpha);
        ctx.shadowBlur = 12 + (1 - depth) * 8;
        for (let i = 0; i < nodes.length; i++) {
          const p = nodes[i];
          ctx.beginPath();
          ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
          ctx.fillStyle = glowNode(0.85 * alpha);
          ctx.fill();
        }
        ctx.shadowBlur = 0;
      });

      rafRef.current = requestAnimationFrame(step);
    };

    step();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, [nodeCount, linkDistance, cursorLinkDistance, layers, speed, hue, saturation, lightness, background, mode]);

  return (
    <div
      style={{
        position: mode === "fixed" ? "fixed" : "absolute",
        inset: 0,
        zIndex: 1,
        pointerEvents: "none",
        background,
      }}
    >
      {/* optional transparent overlay gradient for extra richness */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 600px at 25% 75%, rgba(255,106,0,0.08), rgba(0,0,0,0)), radial-gradient(700px 500px at 85% 20%, rgba(255,106,0,0.05), rgba(0,0,0,0))",
          filter: "saturate(120%)",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}